import { Building2, Ticket, User, type LucideIcon } from 'lucide-react';

export type GuideAudience = 'individual' | 'organizers' | 'organizations';

export type GuideStep = {
  title: string;
  description: string;
};

export type PlatformGuideConfig = {
  audience: GuideAudience;
  label: string;
  navDescription: string;
  pageTitle: string;
  pageSubtitle: string;
  icon: LucideIcon;
  steps: GuideStep[];
  videoCategories: string[];
  quickLinks: { label: string; href: string }[];
};

export const PLATFORM_GUIDES: Record<GuideAudience, PlatformGuideConfig> = {
  individual: {
    audience: 'individual',
    label: 'For Individuals',
    navDescription: 'Discover events, register, and manage tickets',
    pageTitle: 'Individual Guide',
    pageSubtitle:
      'Follow these steps to discover events, register quickly, and manage your tickets from one place.',
    icon: User,
    steps: [
      {
        title: 'Create your account',
        description: 'Sign up with email, verify your profile, and set notification preferences.',
      },
      {
        title: 'Discover events',
        description: 'Browse by category, location, or date from Explore Events and save events to your calendar.',
      },
      {
        title: 'Choose the right ticket',
        description: 'Review ticket types, pricing, and registration requirements before checkout.',
      },
      {
        title: 'Complete registration',
        description: 'Fill in the registration form, apply promo codes if available, and confirm payment when required.',
      },
      {
        title: 'Access your tickets',
        description: 'Open My Tickets from your dashboard to view QR codes, updates, and event announcements.',
      },
      {
        title: 'Join on event day',
        description: 'Use your ticket QR code for check-in and follow live updates from the event page.',
      },
    ],
    videoCategories: ['individual', 'participant', 'attendee', 'registration'],
    quickLinks: [
      { label: 'Browse events', href: '/events' },
      { label: 'Create account', href: '/register/individual' },
      { label: 'Help center', href: '/help' },
    ],
  },
  organizers: {
    audience: 'organizers',
    label: 'For Organizers',
    navDescription: 'Create events, sell tickets, and run operations',
    pageTitle: 'Organizer Guide',
    pageSubtitle:
      'Step-by-step workflows for launching events, managing registrations, and tracking performance.',
    icon: Ticket,
    steps: [
      {
        title: 'Become an organizer',
        description: 'Complete organizer onboarding and verify your profile to unlock event creation.',
      },
      {
        title: 'Create your first event',
        description: 'Use the event wizard to set dates, venue, ticket types, and registration fields.',
      },
      {
        title: 'Configure registration',
        description: 'Set approval rules, team registration, waitlists, and custom form fields.',
      },
      {
        title: 'Publish and promote',
        description: 'Publish your event page, share links, and configure promotional content for attendees.',
      },
      {
        title: 'Manage participants',
        description: 'Track registrations, approve entries, send updates, and export attendee data.',
      },
      {
        title: 'Run check-in and analytics',
        description: 'Use scanner tools on event day and review revenue, attendance, and engagement reports.',
      },
    ],
    videoCategories: ['organizer', 'organizers', 'host', 'event'],
    quickLinks: [
      { label: 'Host an event', href: '/host-events' },
      { label: 'Organizer dashboard', href: '/dashboard/events' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  organizations: {
    audience: 'organizations',
    label: 'For Organizations',
    navDescription: 'Manage teams, events, and governance at scale',
    pageTitle: 'Organization Guide',
    pageSubtitle:
      'Set up your organization workspace, assign organizers, and manage events across departments and clubs.',
    icon: Building2,
    steps: [
      {
        title: 'Register your organization',
        description: 'Submit organization details, branding, and domain preferences during onboarding.',
      },
      {
        title: 'Configure roles and access',
        description: 'Invite team members, assign organization roles, and control who can create or approve events.',
      },
      {
        title: 'Create and assign events',
        description: 'Launch organization events and assign lead organizers or co-hosts from the events dashboard.',
      },
      {
        title: 'Manage promotion and branding',
        description: 'Configure organization profile, event promotion sections, and public-facing content.',
      },
      {
        title: 'Monitor operations',
        description: 'Track registrations, approvals, finance, and analytics across all organization events.',
      },
      {
        title: 'Govern and scale',
        description: 'Use approval workflows, finance rules, and reporting to run events consistently at scale.',
      },
    ],
    videoCategories: ['organization', 'organizations', 'team', 'enterprise'],
    quickLinks: [
      { label: 'Register organization', href: '/register/organization' },
      { label: 'Organization dashboard', href: '/dashboard/organization' },
      { label: 'Organization events', href: '/dashboard/organization/events' },
    ],
  },
};

export const GUIDE_AUDIENCE_ORDER: GuideAudience[] = ['individual', 'organizers', 'organizations'];

export function isGuideAudience(value: string): value is GuideAudience {
  return value in PLATFORM_GUIDES;
}

export function resolveGuideDisplay(
  audience: GuideAudience,
  content?: {
    page_title?: string | null;
    page_subtitle?: string | null;
    steps?: GuideStep[];
  } | null,
): Pick<PlatformGuideConfig, 'pageTitle' | 'pageSubtitle' | 'steps'> {
  const fallback = PLATFORM_GUIDES[audience];
  if (!content) {
    return {
      pageTitle: fallback.pageTitle,
      pageSubtitle: fallback.pageSubtitle,
      steps: fallback.steps,
    };
  }

  return {
    pageTitle: content.page_title?.trim() || fallback.pageTitle,
    pageSubtitle: content.page_subtitle?.trim() || fallback.pageSubtitle,
    steps: content.steps?.length ? content.steps : fallback.steps,
  };
}

export function matchGuideVideoCategory(category: string | null | undefined, audience: GuideAudience): boolean {
  if (!category?.trim()) return audience === 'organizers';
  const normalized = category.trim().toLowerCase();
  return PLATFORM_GUIDES[audience].videoCategories.some(
    (token) => normalized.includes(token) || token.includes(normalized),
  );
}

export const DOCUMENTATION_NAV_ITEMS = [
  {
    name: 'Articles',
    description: 'Explore our latest insights and updates',
    href: '/articles',
  },
  {
    name: 'Help Center',
    description: 'FAQs, guides, and support',
    href: '/help',
  },
] as const;
